import {
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import * as React from "react";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { RobotoDivider } from "@/shared/components/RobotoDivider";
import { RobotoTypography } from "@/shared/components/RobotoTypography";
import { IngestAsAssetDialog } from "@/shared/components/visualization/IngestAsAssetDialog";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { type FileRecord } from "@/shared/domain/files";
import { useOrgFileSearch } from "@/shared/domain/files/hooks";
import {
  ingestableAssetFileTypes,
  readVisualizationAsset,
} from "@/shared/domain/urdf";
import { useResolveVisualizationAssetFile } from "@/shared/domain/urdf/hooks";
import { Button, TextInput } from "@/shared/foundation/primitives";
import { useDebouncedCallback } from "@/shared/hooks";
import { ErrorMonitoringService } from "@/shared/services/ErrorMonitoringService";
import type { File as ThreeDFile } from "@/shared/state/visualization/schema/v1/file";

import { DeviceModelList } from "./DeviceModelList";
import styles from "./ModelFilePicker.module.css";
import { ModelFileRow } from "./ModelFileRow";
import { useAssociationLabels } from "./useAssociationLabels";

/**
 * A chosen model, and the file the user actually chose.
 *
 * The two always differ: the user selects `robot.urdf`, and what the panel must
 * reference is the glTF the platform generated from it — a file under
 * `.VISUALIZATION_ASSETS/` with a derived name nobody has seen. `model` is what
 * gets loaded; `source` is what gets shown, and is the only handle by which a
 * later re-ingest could be noticed.
 *
 * Both fields are therefore required. That is narrower than the mesh schema,
 * where `source` stays optional: a workspace saved when the picker still
 * offered a `.gltf` directly holds a model with no source, and must keep
 * loading.
 */
export interface PickedModel {
  /** The glTF the mesh will reference — the artifact, never the description. */
  model: ThreeDFile;
  /** The robot description or point cloud scan it was generated from. */
  source: ThreeDFile;
}

interface ModelFilePickerProps {
  /** The entity the chosen model will be imported beneath, named in the title. */
  entityId: string;
  onClose: () => void;
  /**
   * Called with the resolved model. The picker keeps itself open until this
   * fires, so the caller is what dismisses it.
   */
  onSubmit: (picked: PickedModel) => void;
  /** Called when the pick could not be resolved to a loadable model. */
  onError: (message: string) => void;
  /**
   * Whether picking an unconverted file may start a conversion.
   *
   * False for a caller without `release-3d-assets`: the list still opens, and
   * already-converted models are still pickable, but nothing here can launch a
   * worker run. See {@link modelReadiness}.
   */
  canIngest: boolean;
}

/** How long typing settles before a new search is issued. */
const SEARCH_DEBOUNCE_MS = 300;

/** The reference the panel stores, from the record the listing returned. */
function toThreeDFile(record: FileRecord): ThreeDFile {
  return {
    fileId: record.file_id,
    relativePath: record.relative_path,
    // Carried so a later staleness check knows which association to look in
    // (a dataset, or a device's or device type's configurations), and so a
    // cross-org load has an association to scope itself to.
    associationId: record.association_id,
  };
}

/**
 * The robot descriptions and point clouds a 3D panel's entity may draw.
 *
 * Mounted only while open, so that the org context it needs is resolved only
 * when someone actually picks a file. Calling those hooks from `MeshConfig`
 * itself would make every entity's mesh form — a plain set of shape controls —
 * depend on being inside an authenticated provider tree.
 *
 * ## Two lists, by feature flag
 *
 * With `release-asset-store` on for the browsing org, the list is the
 * configurations of the devices behind the workspace's data: for each device,
 * its own `.urdf` and `.ply` files and those of its device type, from one
 * inherited listing per device ({@link DeviceModelList}). A workspace whose
 * data names no device gets an explanation of how to give it one.
 *
 * The org-wide list ({@link OrgModelList}) is every robot description in the
 * org. With the flag off it is the only list. With the flag on, a button
 * switches to it, because a robot description uploaded to a dataset is in no
 * device's configurations.
 *
 * ## Why one flat list, and not a dataset browser
 *
 * A two-pane browse, such as the shared `FilePicker` (pick a dataset on the
 * left, then a file on the right), asks the user to already know which dataset
 * holds their robot, which is the one thing they are here to find out. So the
 * org-wide list is one list across every dataset, searched by path.
 *
 * ## What a row has to say
 *
 * Two datasets can each hold a `robot.urdf`, and stacked in one list those rows
 * are identical. Every org-wide row therefore carries its own location (the
 * dataset it lives in, and its dataset-relative path), because either alone is
 * insufficient. A device's row needs only its path, since a device holds one
 * file per path once its type's files are folded in; a row inherited from the
 * type says so, naming the type. The search term is matched against the path
 * in both lists: someone holding `Customer URDF/biped01/robot.urdf` and
 * `Customer URDF/other/robot.urdf` can only separate them by typing part of
 * the directory.
 *
 * ## Why robot descriptions are the only thing listed
 *
 * The generated glTFs are unreachable from here — they live under
 * `.VISUALIZATION_ASSETS/`, and every file query drops paths beginning with a
 * dot before any other condition is applied. Listing the `.urdf` instead is not
 * a workaround for that: the URDF is the artifact of record — what the user
 * uploaded and named, what carries the conversion's status, what a re-upload
 * changes — while the glTF is a derived cache keyed on its upload.
 *
 * A hand-authored `.gltf`/`.glb` sitting in the same dataset is deliberately
 * *not* offered, even though the panel could load it. Such a file has no ingest
 * behind it, so nothing states whether it is current, nothing regenerates it
 * when its source moves, and the joints the settings bind against are whatever
 * happened to be exported. Every model added here is therefore backed by an
 * ingested asset, and every one of them resolves to its artifact on submit:
 * downstream keeps receiving a glTF reference and never learns that robot
 * descriptions exist.
 */
export function ModelFilePicker({
  entityId,
  onClose,
  onSubmit,
  onError,
  canIngest,
}: ModelFilePickerProps) {
  const orgId = useCurrentOrgId();
  const assetStoreEnabled = useCheckFeatureFlag("release-asset-store");
  /** Whether the user switched from the device list to the org-wide one. */
  const [searchingOrg, setSearchingOrg] = React.useState(false);
  const showDeviceList = assetStoreEnabled && !searchingOrg;
  const resolveAssetFile = useResolveVisualizationAssetFile();

  /** What is in the box, updated on every keystroke. */
  const [typed, setTyped] = React.useState("");
  /** What has actually been searched for — `typed`, once it settles. */
  const [searchTerm, setSearchTerm] = React.useState("");
  /** The row whose artifact is being looked up, while that is in flight. */
  const [resolving, setResolving] = React.useState<string | undefined>();
  /**
   * The file the ingest wizard is open on, if any.
   *
   * A row with no artifact behind it is not a dead end here. The wizard is the
   * one way an ingestable source becomes a model, and the picker is one of the
   * places a user discovers they need it, so it is raised over the list rather
   * than sending them back to the file browser to find the same file again.
   */
  const [ingesting, setIngesting] = React.useState<FileRecord | null>(null);
  /**
   * Set when the dialog is dismissed, so a resolution that lands afterwards is
   * dropped instead of reporting a pick the user has already walked away from.
   */
  const dismissedRef = React.useRef(false);

  const applySearchTerm = useDebouncedCallback(
    setSearchTerm,
    SEARCH_DEBOUNCE_MS,
  );

  const handleClose = React.useCallback(() => {
    dismissedRef.current = true;
    onClose();
  }, [onClose]);

  /**
   * Turns a chosen source file into the model the mesh will reference.
   *
   * @param record the robot description or point cloud scan the user chose
   * @param assetRelativePath where the artifact is, when the caller knows
   *   better than the record does — which the wizard does, having just written
   *   it. The record in hand came from a listing issued before the conversion
   *   existed, so its status block still says nothing about the artifact.
   */
  const handlePick = React.useCallback(
    (record: FileRecord, assetRelativePath?: string) => {
      setResolving(record.file_id);

      // Every listed row is an ingestable source, so every pick is a lookup:
      // what the mesh references is the artifact, not the file just clicked.
      // The lookup is scoped to the record's own association and org, which
      // are not necessarily the ones being browsed: a shared dataset is
      // listed under one org and owned by another, and a configuration
      // inherited from a device type belongs to the type, not the device.
      void resolveAssetFile(
        record,
        assetRelativePath ?? readVisualizationAsset(record)?.relative_path,
      )
        .then((artifact) => {
          if (dismissedRef.current) {
            return;
          }
          if (artifact === null) {
            // The row said `completed`, so this is the platform's artifact
            // having gone missing rather than anything the user did wrong.
            setResolving(undefined);
            onError(
              `The 3D model generated from ${record.name} could not be found. Try converting it again.`,
            );
            return;
          }
          onSubmit({
            model: toThreeDFile(artifact),
            source: toThreeDFile(record),
          });
        })
        .catch((error: unknown) => {
          // Deliberately not the underlying message: a transport error names
          // the artifact's path, which is the one string this flow exists to
          // keep off the screen. The detail goes to monitoring instead.
          ErrorMonitoringService.captureError(
            error instanceof Error ? error : new Error(String(error)),
          );
          if (dismissedRef.current) {
            return;
          }
          setResolving(undefined);
          onError(`Could not load the 3D model generated from ${record.name}`);
        });
    },
    [onError, onSubmit, resolveAssetFile],
  );

  /**
   * Sends a row with no artifact behind it through the ingest wizard.
   *
   * The pick is not abandoned by doing so — see {@link handleIngested}. The
   * picker stays mounted underneath, so leaving the wizard leaves the user on
   * the list they were choosing from.
   */
  const handleIngest = React.useCallback((record: FileRecord) => {
    setIngesting(record);
  }, []);

  /**
   * Finishes the pick the conversion was started for.
   *
   * Converting from here is not a detour: the user came to choose a model, and
   * the wizard they were sent through is what produced it. Asking them to find
   * the same row a second time would be asking them to repeat a choice they
   * have already made.
   *
   * The path comes from the wizard rather than from the record, which was
   * listed before the artifact existed and whose status block therefore still
   * says nothing about it.
   */
  const handleIngested = React.useCallback(
    (source: FileRecord, assetRelativePath?: string) => {
      handlePick(source, assetRelativePath);
    },
    [handlePick],
  );

  return (
    <>
      <Dialog
        open
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: styles.dialogPaper }}
      >
        <DialogTitle className={styles.title}>
          <h1 className={styles.titleText}>Choose a model for {entityId}</h1>
          <RobotoTypography className={styles.subtitleText}>
            {showDeviceList
              ? "Robot descriptions (.urdf) and point clouds (.ply) among the configurations of the devices in this workspace, and of their device types."
              : "Robot descriptions (.urdf) and point clouds (.ply) anywhere in this organization."}
          </RobotoTypography>
        </DialogTitle>

        <div className={styles.searchRow}>
          <TextInput
            autoFocus
            type="search"
            aria-label="Search robot descriptions"
            placeholder="Search by file name or path…"
            value={typed}
            onChange={(event) => {
              setTyped(event.target.value);
              applySearchTerm(event.target.value);
            }}
          />
        </div>

        <RobotoDivider />

        <DialogContent className={styles.content}>
          {showDeviceList ? (
            <DeviceModelList
              // Filtered in place as typed: the listing is complete, so there
              // is no request to hold back for the debounce.
              term={typed}
              resolving={resolving}
              canIngest={canIngest}
              onPick={handlePick}
              onIngest={handleIngest}
            />
          ) : (
            <OrgModelList
              orgId={orgId}
              searchTerm={searchTerm}
              // Between a keystroke and the search it triggers, whatever is on
              // screen answers a question the user has already moved off. That
              // is only visible when there is nothing to show: a stale *list*
              // is being narrowed in front of them, but a stale "nothing
              // matched" reads as a definite answer about the term they are
              // still typing.
              settled={typed === searchTerm}
              resolving={resolving}
              canIngest={canIngest}
              onPick={handlePick}
              onIngest={handleIngest}
            />
          )}
        </DialogContent>

        <RobotoDivider />

        <DialogActions className={styles.actions}>
          {assetStoreEnabled && (
            <Button onClick={() => setSearchingOrg((current) => !current)}>
              {searchingOrg
                ? "Show device configurations"
                : "Search the whole organization"}
            </Button>
          )}
          <Button onClick={handleClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
      {/* Mounted after the list, so it stacks over it: two plain MUI dialogs
          order by mount rather than by any z-index of their own. */}
      <IngestAsAssetDialog
        file={ingesting}
        onClose={() => setIngesting(null)}
        onIngested={handleIngested}
      />
    </>
  );
}

interface OrgModelListProps {
  /** The org being browsed, whose files are searched. */
  orgId: string;
  /** The settled search term. */
  searchTerm: string;
  /** Whether what is typed has been searched for yet. */
  settled: boolean;
  resolving: string | undefined;
  onPick: (record: FileRecord) => void;
  onIngest: (record: FileRecord) => void;
  /** Whether an unconverted row may be picked to start a conversion. */
  canIngest: boolean;
}

/**
 * Every robot description and point cloud in the org, whatever holds it,
 * searched server-side and paged: the only list for an org with
 * `release-asset-store` off, and the one "Search the whole organization"
 * switches to when it is on.
 *
 * The four states below are four different things to tell a user, so none of
 * them is collapsed into a blank box. In particular "still asking" is never
 * rendered as an answer: an empty list only becomes "nothing matched" once a
 * settled query has said so.
 */
function OrgModelList({
  orgId,
  searchTerm,
  settled,
  resolving,
  onPick,
  onIngest,
  canIngest,
}: OrgModelListProps) {
  const filesQuery = useOrgFileSearch({
    // The search route answers a missing org with an empty page rather than an
    // error, so this is the difference between "no robots" and a silent
    // scoping bug. It is also the *browsing* org: the org that owns any given
    // result is read back off that record, never assumed to be this one.
    orgId,
    searchTerm,
    // Ingestable sources only: robot descriptions and point cloud scans. A
    // `.gltf`/`.glb` in the same dataset is a model the panel could load, but
    // not one the platform ingested, so it is not on offer (see the note on
    // `ModelFilePicker`). Filtering happens in the query rather than over the
    // response, so an excluded file is never even paged in.
    extensions: ingestableAssetFileTypes,
  });

  const records = React.useMemo(() => filesQuery.data ?? [], [filesQuery.data]);

  const associationIds = React.useMemo(
    () => records.map((record) => record.association_id),
    [records],
  );
  const associationLabel = useAssociationLabels(orgId, associationIds);

  if (filesQuery.error !== null) {
    return (
      <Alert severity="error" className={styles.status}>
        Could not search for robot descriptions. Try closing and reopening this
        dialog.
      </Alert>
    );
  }

  if (filesQuery.isPending || (records.length === 0 && !settled)) {
    return (
      <div className={styles.status}>
        <CircularProgress size={16} />
        <RobotoTypography variant="body2">Searching…</RobotoTypography>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className={styles.status}>
        <RobotoTypography variant="body2" color="text.secondary">
          {searchTerm === ""
            ? "No robot descriptions or point clouds in this organization. Upload a .urdf or .ply to a dataset to make it available here."
            : `No robot descriptions or point clouds match “${searchTerm}”.`}
        </RobotoTypography>
      </div>
    );
  }

  return (
    <>
      <ul className={styles.list}>
        {records.map((record) => (
          <ModelFileRow
            key={record.file_id}
            record={record}
            // What holds the file, and where inside it: the one line that
            // tells two identically named robot descriptions apart.
            location={`${associationLabel(record.association_id)} · ${record.relative_path}`}
            resolving={resolving === record.file_id}
            // While one row is being resolved no other row may be chosen: a
            // second pick would race the first, and whichever lookup returned
            // last would win regardless of which the user asked for second.
            busy={resolving !== undefined}
            canIngest={canIngest}
            onPick={onPick}
            onIngest={onIngest}
          />
        ))}
      </ul>

      {filesQuery.hasNextPage && (
        <Button
          className={styles.loadMore}
          onClick={() => void filesQuery.fetchNextPage()}
          disabled={filesQuery.isFetchingNextPage}
          loading={filesQuery.isFetchingNextPage}
        >
          Load more
        </Button>
      )}
    </>
  );
}
